/**
 * §F Competitive Set — persistence hook, chunk-row backed.
 *
 * `CompetitiveSetSection` is deliberately dumb (presentational, `value`/`onChange`
 * only) so it stays testable in isolation — see its own file header and the
 * 25-test suite pinned against exactly that contract. This hook is the piece
 * that used to be missing: it maps `CompetitorRecord[]` to and from bb_chunks
 * rows (section='F', kind='competitor') using the EXISTING `upsertEntry` /
 * `discardEntry` actions on the wizard store (see brandBibleWizardStore.ts —
 * this file does not reimplement or modify them), so the section can persist
 * without becoming any less testable.
 *
 * ACCEPTANCE CRITERION 5 (non-negotiable, per the approved §F design): this
 * hook issues ZERO blocklist or link-policy calls. It does not import
 * anything from a blocklist/link-policy module, and never will — competitor
 * intelligence here is plain CRUD against bb_chunks, nothing else.
 *
 * FIELD MAPPING — the ONE place this mapping is defined, mirroring
 * registry.py's `bb_chunks.columns` and projection.py's `_competitor_entry`
 * composer exactly. Do not diverge from either without updating both sides.
 *
 *   title          = name
 *   body           = domain || name       // NOT NULL; matches _competitor_entry
 *   why            = our_angle
 *   position_note  = position_note        // own column, 1:1, no packing
 *   public_mention = false                // always, on every CREATE path
 *   section        = 'F'
 *   kind           = 'competitor'
 *
 * `position_note` now has its own `bb_chunks.position_note` column — the
 * ruling this file used to be waiting on has landed. It is a first-class
 * persisted field exactly like `why`: written on CREATE, diffed into the
 * PATCH like every other mapped field, and read back in `fromChunk`. It is
 * still DELIBERATELY never written to `channel` (that column is §I's,
 * already spoken for by ChannelRulesPanel's headline/CTA channel tagging)
 * and never concatenated into `body`/`why` — own column, own field, no
 * overloading, no delimiter packing.
 *
 * There is no local React state backing `position_note` anymore. The old
 * `positionNotes` state (and the temp-id-to-server-id reconciliation that
 * came with it) existed only to paper over the missing column; with a real
 * column, the store's chunk list is the only source of truth, and a second
 * client-side copy would just be a second place the value could go stale or
 * disagree with the server. That machinery is gone, not merely disabled.
 *
 * `public_mention` stays excluded from `toPatchPayload` on purpose — see
 * that function's own comment. The backend still force-resets it on every
 * write path (create and patch alike), so emitting it would be a silent
 * no-op; that exclusion is unrelated to `position_note` and is untouched by
 * this change.
 *
 * TYPING NOTE: `ChunkPayload`/`BbEntry<'chunks'>` (in
 * features/brand-bible-wizard/types.ts) do not yet declare `position_note`
 * in their shared TypeScript shape — widening that shared type is outside
 * this file's edit scope for this change. `CompetitorChunkPayload` and
 * `CompetitorChunk` below intersect the shared types with `{ position_note?:
 * string }` purely for local typing; the wire field name and semantics are
 * identical to the shared type's other fields, this is not a second payload
 * contract.
 *
 * CONFIRM / REJECT SEMANTICS (Direction-4 selectable-chips review model):
 *
 *   confirm(id) / confirmMany(ids) persist through the wizard store's
 *   EXISTING `verifyEntry` action (POST .../verify, approve_for_public
 *   always left at its default false — competitor chunks are internal-only
 *   and the backend force-resets public_mention anyway, so this path can
 *   never approve one for public mention). This is exactly how verification
 *   persists everywhere else in the wizard: the server stamps the real
 *   acting user into verified_by/verified_at, and `fromChunk` below already
 *   derives `record.verified` / `record.last_verified` from those columns,
 *   so a confirm lands in the same shape the UI already reads. Confirm is
 *   idempotent here: an already-verified row is skipped rather than
 *   re-stamped, so a bulk confirm can't silently rewrite the verification
 *   timestamp (which drives the section's staleness signal) of rows nobody
 *   re-reviewed.
 *
 *   reject(id) / rejectMany(ids) persist as a SOFT DELETE through the
 *   existing `discardEntry` action (DELETE .../entries/... — contract §4.4
 *   is soft-delete-only; there is no hard-removal endpoint to call even if
 *   one were wanted). This is a deliberate decision, not a silent collapse
 *   of "reject" into "delete":
 *     - A dedicated `rejected` state was NOT available: no `status`/
 *       `rejected_at` column or endpoint exists on the bb_chunks surface,
 *       and backend/migration files are outside this change's scope, so a
 *       new state cannot be persisted from here, only simulated in local
 *       state that would vanish on reload — a dishonest reject.
 *     - Hard removal was NOT available: the only delete the service exposes
 *       is soft (deleted_at stamp).
 *     - Soft delete is the strongest honest signal the existing API can
 *       persist: the row leaves every live view (this hook already filters
 *       `deleted_at === null`) yet stays in the table — recoverable,
 *       auditable, and distinguishable from never-existed. A rejected
 *       suggestion also stops re-appearing in needs-review counts, which is
 *       the behaviour the review model actually needs.
 *   Consequence to be aware of: single "Not a competitor" and the chip's x
 *   remove currently land on the same persistence (soft delete). They differ
 *   in intent at the UI layer; the API cannot yet record that distinction.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, ChunkPayload } from '@/features/brand-bible-wizard/types';
import type { CompetitorRecord } from './CompetitiveSetSection';

// See "TYPING NOTE" in the module doc above — these locally widen the shared
// chunk types to add `position_note` for typing purposes only. `types.ts` is
// out of scope for this change.
type CompetitorChunk = BbEntry<'chunks'> & { position_note?: string };
type CompetitorChunkPayload = ChunkPayload & { position_note?: string };

/**
 * The CompetitorRecord <-> bb_chunks(section='F', kind='competitor') mapping.
 * Grouped into one exported constant so a future reader (or the eventual
 * `position_note` ruling) has exactly one place to change. See the module
 * doc above for the full rationale.
 */
export const COMPETITOR_CHUNK_MAP = {
  /**
   * Composes the CREATE (POST) payload. `public_mention` is hard-pinned to
   * `false` here — never read off the record — so a create can never land a
   * publicly-mentionable competitor by accident (race, copy/paste, or a
   * future caller that forgets to default it). This is the standing
   * internal-by-default rule the component's own header comment describes.
   */
  toCreatePayload(
    record: Pick<CompetitorRecord, 'name' | 'domain' | 'our_angle' | 'position_note'>,
  ): CompetitorChunkPayload {
    const name = record.name.trim();
    return {
      section: 'F',
      kind: 'competitor',
      title: name,
      body: record.domain?.trim() || name,
      why: record.our_angle,
      position_note: record.position_note,
      public_mention: false,
    };
  },

  /**
   * Composes a merge-PATCH payload — only the mapped fields that actually
   * changed between `prev` and `next`, same discipline ChannelRulesPanel uses
   * for its channel-tag PATCH (send changed subkeys only, never the whole
   * record). `position_note` is diffed exactly like `our_angle` now that it
   * has a real column — a position_note-only edit produces a real one-field
   * patch, not a no-op. Returns `null` when nothing mapped changed.
   */
  toPatchPayload(prev: CompetitorRecord, next: CompetitorRecord): CompetitorChunkPayload | null {
    const patch: CompetitorChunkPayload = {};
    let dirty = false;
    if (prev.name !== next.name || (prev.domain ?? '') !== (next.domain ?? '')) {
      const name = next.name.trim();
      patch.title = name;
      patch.body = next.domain?.trim() || name;
      dirty = true;
    }
    if ((prev.our_angle ?? '') !== (next.our_angle ?? '')) {
      patch.why = next.our_angle;
      dirty = true;
    }
    if ((prev.position_note ?? '') !== (next.position_note ?? '')) {
      patch.position_note = next.position_note;
      dirty = true;
    }
    // public_mention is deliberately NOT diffed into the patch. The backend
    // (entries.py, both `_write_entry` and `patch_entry`) unconditionally
    // forces `public_mention = False` for bb_chunks rows where kind ===
    // 'competitor', on every write path, with no exception. Emitting it here
    // would be a silent no-op — the exact trap this hook exists to avoid (see
    // module doc). Whoever builds a real approve-for-public backend path for
    // competitor chunks should add this branch back deliberately, together
    // with the UI control that lets an operator trigger it.
    return dirty ? patch : null;
  },

  /**
   * Reverses the mapping — a stored chunk row back to the presentational
   * `CompetitorRecord` shape. Mirrors projection.py's `_competitor_entry`
   * exactly: `domain` only appears when `body` differs from the resolved
   * `name`. `position_note` now comes straight off the chunk row like every
   * other field — no side-channel local state to consult anymore.
   */
  fromChunk(chunk: CompetitorChunk): CompetitorRecord {
    const name = chunk.title ?? chunk.body ?? '';
    const domain = chunk.body && chunk.body !== name ? chunk.body : undefined;
    return {
      id: chunk.id,
      name,
      domain,
      our_angle: chunk.why ?? undefined,
      position_note: chunk.position_note ?? undefined,
      public_mention: chunk.public_mention ?? false,
      source: chunk.source === 'discovered' || chunk.source === 'intake' ? chunk.source : 'client_edit',
      verified: chunk.verified_by !== null,
      last_verified: chunk.verified_at ?? undefined,
    };
  },
};

/**
 * Outcome of a bulk confirm/reject. A partial failure is a RESULT, not an
 * exception: `confirmMany`/`rejectMany` never throw for a per-id failure, they
 * report it here so the caller can surface an honest "N saved · M failed —
 * retry" instead of being told the whole batch failed while some records
 * silently persisted. `failedIds` is exactly the subset safe to retry.
 */
export interface CompetitiveBulkResult {
  succeeded: number;
  failed: number;
  failedIds: string[];
}

/**
 * The buffered edit set an explicit-save §F commits in one shot. Deliberately
 * ID-KEYED — never a whole-list snapshot — so a commit can NEVER synthesize a
 * delete by diffing list contents (the §B prod data-loss incident). A removal
 * is ONLY ever an explicit id in `removedIds`; an add is a temp-id record in
 * `added`; an edit is the desired next record in `edited`, whose PATCH is diffed
 * against the LIVE store row of the same id at commit time.
 */
export interface CompetitiveBufferedCommit {
  /** Temp-id records to CREATE (the store swaps in the real server row/id). */
  added: CompetitorRecord[];
  /** Existing rows whose mapped fields changed — patched against the live base. */
  edited: CompetitorRecord[];
  /** Explicit ids to soft-delete (id-keyed; never derived from a list diff). */
  removedIds: string[];
}

export interface UseCompetitiveSetResult {
  records: CompetitorRecord[];
  onChange: (next: CompetitorRecord[]) => void;
  /**
   * Confirm a single competitor — persists via the store's `verifyEntry`
   * (server-stamped verified_by/verified_at; see the module doc's CONFIRM /
   * REJECT SEMANTICS). Idempotent: already-confirmed and unknown ids no-op.
   */
  confirm: (id: string) => Promise<void>;
  /**
   * Reject a single competitor ("not a competitor") — persists as a SOFT
   * DELETE via the store's `discardEntry`, the strongest reject the existing
   * API can record (see the module doc for why this is not a silent delete).
   */
  reject: (id: string) => Promise<void>;
  /**
   * Bulk confirm — same per-id semantics as `confirm`, but the ids are
   * processed ONE AT A TIME (sequential, never a parallel burst) and each id
   * is isolated: one failure never abandons the rest. Resolves to a tally
   * ({ succeeded, failed, failedIds }); never rejects for a partial failure.
   */
  confirmMany: (ids: string[]) => Promise<CompetitiveBulkResult>;
  /**
   * Bulk reject — same per-id semantics as `reject`, processed ONE AT A TIME
   * with each id isolated. Resolves to a tally ({ succeeded, failed,
   * failedIds }); never rejects for a partial failure.
   */
  rejectMany: (ids: string[]) => Promise<CompetitiveBulkResult>;
  /**
   * Commit a buffered edit set (explicit-save §F) through the SAME
   * `upsertEntry`/`discardEntry` store actions `onChange` already uses — but
   * driven by an ID-KEYED op set, never a diffed list. Removals are the exact
   * ids in `removedIds` (soft delete); edits are diffed against the live store
   * row (a row that has since left the store is skipped, never recreated); adds
   * are one CREATE each. Resolves `true` only when every op succeeded; `false`
   * if any failed (each op is isolated in its own try/catch). NEVER throws — a
   * failed commit lets the caller keep the buffer intact rather than lose it.
   */
  commitBuffered: (ops: CompetitiveBufferedCommit) => Promise<boolean>;
}

/**
 * Confirm/reject a batch of §F records ONE AT A TIME — the discipline the
 * shared `VerifiableRecordList` "verify all" primitive already proves
 * (collection/VerifiableRecordList.tsx). Sequential is deliberate, not
 * incidental:
 *   - No parallel burst. The backend has been observed timing out at 60s on a
 *     SINGLE save; firing 20 selected chips as 20 simultaneous writes (the old
 *     `Promise.all` fan-out) is actively harmful.
 *   - No silent partial state. Under the old fan-out the first rejection
 *     abandoned the batch while the other, un-awaited writes kept going — the
 *     operator was told it failed while some records saved anyway. Here every
 *     id is wrapped in its own try/catch, so one failure never abandons the
 *     rest, and the outcome is RETURNED as a tally rather than thrown.
 *
 * `op` is idempotent, so retrying just the returned `failedIds` is safe:
 * `confirm` skips already-verified rows, and `reject` no-ops on a row that has
 * already left the live set (soft-deleted) — neither can double-apply.
 */
async function settleSequentially(
  ids: string[],
  op: (id: string) => Promise<void>,
): Promise<CompetitiveBulkResult> {
  const result: CompetitiveBulkResult = { succeeded: 0, failed: 0, failedIds: [] };
  for (const id of ids) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential on purpose: one write at a time, never a parallel burst against a backend that has timed out at 60s on a single save.
      await op(id);
      result.succeeded += 1;
    } catch {
      result.failed += 1;
      result.failedIds.push(id);
    }
  }
  return result;
}

/**
 * The two review groups the Direction-4 UI renders: records the machine
 * proposed vs records a person typed in. Derived from the existing
 * `CompetitorRecord.source` field (populated by `fromChunk` off the chunk
 * row's own `source` column) — no new field is introduced:
 *
 *   'suggested'  <- source === 'discovered'   ("Suggested automatically")
 *   'entered'    <- source === 'intake' | 'client_edit'   ("Entered by a person")
 *
 * Known limitation, documented for the test slice: `BbSource` also allows
 * 'inferred' and 'performance', but `fromChunk` has always narrowed anything
 * that is not 'discovered'/'intake' to 'client_edit' (CompetitorRecord's
 * `source` union only has those three values, and widening it lives in the
 * component file, outside this change). Such rows therefore group as
 * 'entered' even though they were machine-originated — the fold-in is
 * pre-existing, not introduced here.
 */
export type CompetitorSourceGroup = 'suggested' | 'entered';

export function competitorSourceGroup(
  record: Pick<CompetitorRecord, 'source'>,
): CompetitorSourceGroup {
  return record.source === 'discovered' ? 'suggested' : 'entered';
}

/**
 * The confirmed / needs-review filter discriminator. Reads the existing
 * `verified` flag `fromChunk` derives from the server-stamped `verified_by`
 * column — `confirmed` = verified, `needs review` = not verified.
 */
export function isCompetitorConfirmed(record: Pick<CompetitorRecord, 'verified'>): boolean {
  return record.verified === true;
}

/**
 * Default persistence for §F. Reads/writes ONLY through the wizard store's
 * existing `upsertEntry`/`discardEntry` — no direct service or fetch calls
 * live here, so the "zero network" discipline the component's own test suite
 * pins for its presentational mode is preserved one layer up: this hook's
 * only network surface is the store actions every other section already uses.
 */
export function useCompetitiveSet(): UseCompetitiveSetResult {
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const chunks = useBrandBibleWizardStore(selectEntries('chunks'));

  // A record freshly added by the component carries a client-generated temp
  // id (CompetitiveSetSection's `makeId()`) until the CREATE resolves and the
  // store swaps in the server row under its real id. This ref guards against
  // firing a second CREATE for the same temp id on a rapid re-render before
  // the first resolves — general create-concurrency safety, unrelated to
  // position_note (which no longer needs any reconciliation: it now rides
  // along on the same chunk row the store already swaps in under the real
  // id, like every other field).
  const pendingCreates = useRef<Set<string>>(new Set());

  const competitorChunks = useMemo(
    () => chunks.filter((c) => c.deleted_at === null && c.section === 'F' && c.kind === 'competitor'),
    [chunks],
  );

  const records = useMemo(
    () => competitorChunks.map((c) => COMPETITOR_CHUNK_MAP.fromChunk(c)),
    [competitorChunks],
  );

  const onChange = useCallback(
    (next: CompetitorRecord[]) => {
      const prevById = new Map(records.map((r) => [r.id, r]));
      const nextIds = new Set(next.map((r) => r.id));

      // Removals — a record that existed before this call but is missing now.
      for (const prev of records) {
        if (!nextIds.has(prev.id)) {
          void discardEntry('chunks', prev.id, bbTenant);
        }
      }

      for (const record of next) {
        const prev = prevById.get(record.id);
        if (!prev) {
          if (pendingCreates.current.has(record.id)) continue;
          const tempId = record.id;
          pendingCreates.current.add(tempId);
          void upsertEntry('chunks', COMPETITOR_CHUNK_MAP.toCreatePayload(record), undefined, bbTenant).then(() => {
            pendingCreates.current.delete(tempId);
          });
          continue;
        }

        const patch = COMPETITOR_CHUNK_MAP.toPatchPayload(prev, record);
        if (patch) {
          void upsertEntry('chunks', patch, record.id, bbTenant);
        }
      }
    },
    [records, upsertEntry, discardEntry],
  );

  const confirm = useCallback(
    (id: string): Promise<void> => {
      const chunk = competitorChunks.find((c) => c.id === id);
      // Unknown id (stale selection, or a record rejected in the meantime)
      // is a no-op rather than a 404 the caller has to handle.
      if (!chunk) return Promise.resolve();
      // Idempotent: never re-stamp verified_by/verified_at on a row that is
      // already confirmed — a bulk confirm must not silently move the
      // staleness clock on records nobody re-reviewed.
      if (chunk.verified_by !== null) return Promise.resolve();
      // approve_for_public stays at its default false — §F competitor
      // chunks are internal-only and the backend force-resets the flag
      // anyway (see module doc); confirm here can never approve for public.
      return verifyEntry('chunks', id, undefined, bbTenant);
    },
    [competitorChunks, verifyEntry],
  );

  const reject = useCallback(
    (id: string): Promise<void> => {
      const chunk = competitorChunks.find((c) => c.id === id);
      if (!chunk) return Promise.resolve();
      // Soft delete — the strongest reject the existing API can persist
      // (see the module doc's CONFIRM / REJECT SEMANTICS for the decision).
      return discardEntry('chunks', id, bbTenant);
    },
    [competitorChunks, discardEntry],
  );

  const confirmMany = useCallback(
    (ids: string[]): Promise<CompetitiveBulkResult> => settleSequentially(ids, confirm),
    [confirm],
  );

  const rejectMany = useCallback(
    (ids: string[]): Promise<CompetitiveBulkResult> => settleSequentially(ids, reject),
    [reject],
  );

  const recordsById = useMemo(() => new Map(records.map((r) => [r.id, r])), [records]);

  const commitBuffered = useCallback(
    async (ops: CompetitiveBufferedCommit): Promise<boolean> => {
      let ok = true;
      // Removals FIRST, each an id-keyed soft delete of that EXACT id — never a
      // delete synthesized from diffing list contents (the §B prod data-loss
      // lesson: a removal is only ever an explicit remove of a known id).
      for (const id of ops.removedIds) {
        try {
          // eslint-disable-next-line no-await-in-loop -- one write at a time, never a parallel burst against a backend that has timed out at 60s on a single save.
          await discardEntry('chunks', id, bbTenant);
        } catch {
          ok = false;
        }
      }
      // Edits: a merge-PATCH diffed against the LIVE store row of the same id.
      // A row that has since left the store (e.g. rejected concurrently) is
      // skipped — an edit must NEVER resurrect a deleted competitor.
      for (const next of ops.edited) {
        const prev = recordsById.get(next.id);
        if (!prev) continue;
        const patch = COMPETITOR_CHUNK_MAP.toPatchPayload(prev, next);
        if (!patch) continue;
        try {
          // eslint-disable-next-line no-await-in-loop -- sequential on purpose (see above).
          await upsertEntry('chunks', patch, next.id, bbTenant);
        } catch {
          ok = false;
        }
      }
      // Adds: one CREATE per buffered record; the store swaps in the server row.
      for (const rec of ops.added) {
        try {
          // eslint-disable-next-line no-await-in-loop -- sequential on purpose (see above).
          await upsertEntry('chunks', COMPETITOR_CHUNK_MAP.toCreatePayload(rec), undefined, bbTenant);
        } catch {
          ok = false;
        }
      }
      return ok;
    },
    [recordsById, discardEntry, upsertEntry],
  );

  return { records, onChange, confirm, reject, confirmMany, rejectMany, commitBuffered };
}
